import { Trash2, MessageCircle, Phone } from "lucide-react";
import { LEAD_STATUSES } from "@/data/leads";

const STATUS_CLS = {
    new: "border-gold/60 text-gold",
    contacted: "border-ivory/40 text-ivory/80",
    closed: "border-ivory/15 text-ivory/35",
};

const fmt = (iso) =>
    new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const waHref = (phone) => `https://wa.me/${phone.replace(/[^0-9]/g, "")}`;

const LeadRow = ({ lead, onStatus, onDelete }) => (
    <li data-testid={`lead-row-${lead.id}`} className="grid grid-cols-1 gap-4 border-b border-ivory/10 py-6 md:grid-cols-12 md:items-start md:gap-6">
        <div className="md:col-span-3">
            <p data-testid="lead-name" className="font-serif text-xl text-ivory">{lead.name}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/35">
                {fmt(lead.created_at)} · via {lead.source}
            </p>
        </div>
        <div className="md:col-span-3">
            <p className="text-sm text-ivory/80">{lead.phone}</p>
            {lead.email && <p className="mt-1 break-all text-sm text-ivory/50">{lead.email}</p>}
            <div className="mt-3 flex gap-2">
                <a data-testid="lead-whatsapp-link" href={waHref(lead.phone)} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="flex h-8 w-8 items-center justify-center rounded-full border border-ivory/15 text-ivory/60 hover:border-gold hover:text-gold">
                    <MessageCircle size={13} />
                </a>
                <a data-testid="lead-call-link" href={`tel:${lead.phone.replace(/[^0-9+]/g, "")}`} aria-label="Call" className="flex h-8 w-8 items-center justify-center rounded-full border border-ivory/15 text-ivory/60 hover:border-gold hover:text-gold">
                    <Phone size={13} />
                </a>
            </div>
        </div>
        <div className="md:col-span-4">
            <p className="text-xs uppercase tracking-[0.15em] text-ivory/50">
                {[lead.project_type, lead.budget].filter(Boolean).join(" · ") || "—"}
            </p>
            {lead.message && <p className="mt-2 text-sm font-light leading-relaxed text-ivory/70">{lead.message}</p>}
        </div>
        <div className="flex items-center gap-3 md:col-span-2 md:justify-end">
            <select
                data-testid="lead-status-select"
                value={lead.status}
                onChange={(e) => onStatus(lead.id, e.target.value)}
                className={`rounded-full border bg-transparent px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] focus:outline-none ${STATUS_CLS[lead.status]}`}
            >
                {LEAD_STATUSES.map((s) => (
                    <option key={s.id} value={s.id} className="bg-night text-ivory">{s.label}</option>
                ))}
            </select>
            <button data-testid="lead-delete-btn" onClick={() => onDelete(lead.id)} aria-label="Delete lead" className="flex h-8 w-8 items-center justify-center rounded-full border border-ivory/15 text-ivory/40 transition-colors hover:border-terra hover:text-terra">
                <Trash2 size={13} />
            </button>
        </div>
    </li>
);

export const LeadsTable = ({ leads, onStatus, onDelete }) => {
    if (leads.length === 0) {
        return (
            <p data-testid="leads-empty" className="border-y border-ivory/10 py-16 text-center font-serif text-xl italic text-ivory/40">
                No enquiries here yet.
            </p>
        );
    }
    return (
        <ul data-testid="leads-list" className="border-t border-ivory/10">
            {leads.map((l) => <LeadRow key={l.id} lead={l} onStatus={onStatus} onDelete={onDelete} />)}
        </ul>
    );
};
